/**
 * Colour, derived rather than tabulated.
 *
 * The runtime had no colour at all until now: `palette` was threaded from the
 * spec through the IR and resolved to nothing, so every generated layout landed
 * on the browser's defaults. That is the single biggest reason the product
 * looked like an unstyled document — no amount of layout intelligence survives
 * having no surface, no border and no text colour.
 *
 * The obvious fix is a table of hex values per palette. It is also the wrong
 * one, twice over:
 *
 *  - It is exactly the hardcoded design decision Morph exists to replace. A
 *    ramp somebody typed is a taste claim with no stated reasoning, and the
 *    parameter registry in `packages/generation` would reject it on sight.
 *  - It cannot be checked. Hand-picked ramps meet contrast because someone
 *    eyeballed them once; a derived ramp meets contrast because the lightness
 *    curve is chosen to, and a test can prove it for every combination.
 *
 * So a palette names a **character** — a hue, a chroma, and whether it is a
 * high-contrast variant — and the twelve steps are derived from a lightness
 * curve in OKLCH, which is perceptually uniform. Equal steps look equal, which
 * is the property that makes a derived ramp viable at all; the same curve in
 * HSL produces ramps that are visibly lumpy in the middle.
 *
 * Step roles follow the convention Radix Colors established, because it is a
 * solved problem and re-deriving the *semantics* would be arrogance rather than
 * rigour. We take the structure and derive the values:
 *
 *   1–2   page and raised backgrounds
 *   3–5   component surfaces: rest, hover, active
 *   6–8   borders: subtle, interactive, strong
 *   9–10  solid fills
 *   11    secondary text — must clear 4.5:1 on steps 1–2
 *   12    primary text — must clear 7:1 on steps 1–2
 */

import type { Palette } from '@morph/spec';

export type Scheme = 'light' | 'dark';

/** What a palette name actually asserts. Hue in degrees, chroma as a ceiling. */
const CHARACTER: Record<Palette, { hue: number; chroma: number; contrastBoost: number }> = {
  // Not zero chroma. A truly neutral grey next to any coloured content reads
  // as dirty; a trace of the page's own hue is what makes greys look intentional.
  neutral:         { hue: 265, chroma: 0.006, contrastBoost: 0 },
  warm:            { hue: 55,  chroma: 0.018, contrastBoost: 0 },
  cool:            { hue: 235, chroma: 0.020, contrastBoost: 0 },
  // Chroma near zero and the curve pushed to both ends. High contrast is not
  // "the same palette, darker text" — colour itself is the thing being spent.
  'high-contrast': { hue: 265, chroma: 0.002, contrastBoost: 1 },
};

/**
 * The lightness curve, in OKLCH L (0..1), for a light scheme.
 *
 * Not linear. Backgrounds need to be close together or the page looks striped;
 * text needs to be far from everything or it does not read. The spacing follows
 * that requirement rather than an even division.
 */
const LIGHT_L = [0.995, 0.977, 0.952, 0.928, 0.900, 0.845, 0.760, 0.645, 0.560, 0.515, 0.470, 0.255];
/** Dark is not the light curve reversed. Perceived contrast is asymmetric:
 *  light text on dark reads heavier than dark text on light at equal ratio, so
 *  the dark ramp holds its text steps back rather than pushing them to white. */
const DARK_L  = [0.178, 0.205, 0.248, 0.283, 0.317, 0.363, 0.425, 0.510, 0.640, 0.690, 0.770, 0.945];

export interface Ramp {
  /** Twelve steps, 1-indexed in the docs above and 0-indexed here. */
  readonly steps: readonly string[];
  readonly scheme: Scheme;
  readonly palette: Palette;
}

/* ---------------------------------------------------------------- colour math */

/** OKLCH → linear sRGB → sRGB. Needed so contrast is computed, not assumed. */
export function oklchToRgb(L: number, C: number, hDeg: number): [number, number, number] {
  const h = (hDeg * Math.PI) / 180;
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);

  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  const l = l_ ** 3, m = m_ ** 3, s = s_ ** 3;

  const lin = [
    +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  ];

  return lin.map((v) => {
    const c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.abs(v) ** (1 / 2.4) - 0.055;
    return Math.min(255, Math.max(0, Math.round(c * 255)));
  }) as [number, number, number];
}

const hex = ([r, g, b]: [number, number, number]) =>
  `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;

/** WCAG 2.x relative luminance, from sRGB 0..255. */
export function luminance([r, g, b]: [number, number, number]): number {
  const f = (v: number) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/** WCAG contrast ratio between two sRGB triples. 1..21. */
export function contrast(a: [number, number, number], b: [number, number, number]): number {
  const la = luminance(a), lb = luminance(b);
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/* -------------------------------------------------------------------- ramps */

export function buildRamp(palette: Palette, scheme: Scheme): Ramp {
  const { hue, chroma, contrastBoost } = CHARACTER[palette];
  const base = scheme === 'light' ? LIGHT_L : DARK_L;

  // The page end of the ramp, which the boost pushes away from.
  const anchor = base[0]!;

  const steps = base.map((L, i) => {
    // High contrast increases *separation from the page*, and only for the
    // steps that carry meaning against it — borders, fills and text. Boosting
    // the background steps too was the first attempt and it collapsed steps 1
    // and 2 onto the same value at the clamp: a ramp with a duplicate is not a
    // ramp, and the monotonicity test said so immediately.
    const pushed = contrastBoost && i >= 5
      ? anchor + (L - anchor) * (1 + 0.22 * contrastBoost)
      : L;
    const clamped = Math.min(0.995, Math.max(0.08, pushed));
    // Chroma tapers at the light and dark ends: full-chroma near-white and
    // near-black both read as a colour cast rather than as a neutral surface.
    const taper = 1 - Math.abs(clamped - 0.55) / 0.55;
    const C = chroma * Math.max(0.25, taper) * (i >= 8 ? 2.4 : 1);
    return hex(oklchToRgb(clamped, C, hue));
  });

  return { steps, scheme, palette };
}

/** Semantic names, so the renderer never reaches for a step number. Which step
 *  plays which role is the part we take from Radix; the values are ours. */
export interface Surface {
  readonly page: string;
  readonly raised: string;
  readonly surface: string;
  readonly surfaceHover: string;
  readonly borderSubtle: string;
  readonly border: string;
  readonly borderStrong: string;
  readonly solid: string;
  readonly textMuted: string;
  readonly text: string;
  /**
   * THREE ROLES THAT ARE NOT `solid`, task 0173 slice 4b, ruled by @Codex.
   *
   * Until now every look's links, focus outlines and selected-navigation
   * colour were all `solid` — either directly or through a private alias
   * (`--ws-accent: var(--solid)`, `--nt-blue: var(--solid)`). One value
   * played three roles, which is the same defect as a literal in an
   * app-scoped rule: a look that wants a violet link and a grey focus ring
   * cannot say so without a new selector.
   *
   * @Codex's ruling on the token mapping split them by evidence rather than
   * by symmetry. `--ws-accent` was measured carrying focus outlines AND
   * content links from one property; `selectionAccent` is a role Coda
   * supplies on its own — its buttons are red and its selected page-tree row
   * is blue, simultaneously, so calling both `accent` would lose information
   * the look already has.
   *
   * Every value below defaults to `solid`, so introducing them changed no
   * pixel. They exist so a look CAN differ, not because one does yet.
   */
  /** Links, open slots, reference chips — content the reader may follow. */
  readonly accent: string;
  /** The keyboard focus outline. Not a content colour: it is chrome. */
  readonly focusRing: string;
  /** Selected NAVIGATION — the lit row in a page tree or sidebar. */
  readonly selectionAccent: string;
}

export function surfaceOf(palette: Palette, scheme: Scheme): Surface {
  const s = buildRamp(palette, scheme).steps;
  return {
    page: s[0]!, raised: s[1]!, surface: s[2]!, surfaceHover: s[3]!,
    borderSubtle: s[5]!, border: s[6]!, borderStrong: s[7]!,
    solid: s[8]!, textMuted: s[10]!, text: s[11]!,
    // A derived ramp has one solid step and no basis for inventing a second,
    // so all three roles take it. A TEMPLATE may differ; a palette may not.
    accent: s[8]!, focusRing: s[8]!, selectionAccent: s[8]!,
  };
}

/** Parse `#rrggbb` back to a triple, so tests can assert contrast on output. */
export function parseHex(h: string): [number, number, number] {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
